import { loadMicroApp, initGlobalState } from 'qiankun'
import { message } from 'ant-design-vue'
import domStableConfig from '@/config/domStableConfig'

/**
 * 微应用管理器 - 统一管理微应用的加载、卸载和状态
 * 简化版实现，专注于核心功能和性能优化
 */
class MicroAppManager {
  constructor() {
    this.microApps = new Map() // 存储已加载的微应用实例
    this.loadPromises = new Map() // 存储加载Promise，避免重复加载
    this.preloadedApps = new Set() // 存储已预加载的应用
    this.globalState = null
    this.loadTimeout = 15000 // 15秒超时
    this.retryCount = 3 // 最大重试次数

    // 简化的失败计数机制（替代复杂的熔断器）
    this.failureCounts = new Map()
    this.maxFailures = 5 // 最大连续失败次数

    // LRU缓存相关
    this.maxCacheSize = 10 // 最大缓存应用数量
    this.accessOrder = [] // 访问顺序，用于LRU

    // 加载进度回调
    this.progressCallbacks = new Map()

    // 降级处理函数
    this.degradeHandlers = new Map()

    // 缓存清理相关
    this.cacheCleanupInterval = null
    this.startCacheCleanup()

    // DOM 稳定性检测配置
    this.domStableConfig = domStableConfig

    // 存储当前 MutationObserver 实例
    this.currentObserver = null
  }

  /**
   * 注册加载进度回调
   * @param {string} appName 应用名称
   * @param {Function} callback 回调函数
   */
  registerProgressCallback(appName, callback) {
    if (typeof callback === 'function') {
      this.progressCallbacks.set(appName, callback);
    }
  }

  /**
   * 移除加载进度回调
   * @param {string} appName 应用名称
   */
  unregisterProgressCallback(appName) {
    this.progressCallbacks.delete(appName);
  }

  /**
   * 注册降级处理函数
   * @param {string} appName 应用名称
   * @param {Function} callback 回调函数
   */
  registerDegradationHandler(appName, callback) {
    if (typeof callback === 'function') {
      // 使用 degradeHandlers 作为降级处理函数的存储
      if (!this.degradeHandlers) {
        this.degradeHandlers = new Map();
      }
      this.degradeHandlers.set(appName, callback);
    }
  }

  /**
   * 移除降级处理函数
   * @param {string} appName 应用名称
   */
  unregisterDegradationHandler(appName) {
    if (this.degradeHandlers) {
      this.degradeHandlers.delete(appName);
    }
  }

  /**
   * 报告加载进度
   * @private
   */
  _reportProgress(appName, progress, message) {
    const callback = this.progressCallbacks.get(appName);
    if (callback) {
      try {
        callback(progress, message);
      } catch (error) {
        console.error(`进度回调执行失败: ${appName}`, error);
      }
    }
  }

  /**
   * 清理无效缓存 (简化版)
   */
  async cleanupInvalidCache() {
    console.log('开始清理微应用缓存...')
    const appsToRemove = []

    for (const [name, app] of this.microApps) {
      try {
        const status = app.getStatus()
        // 只移除明确损坏的应用
        if (status === 'SKIP_BECAUSE_BROKEN') {
          appsToRemove.push(name)
        }
      } catch (error) {
        console.error(`检查微应用 ${name} 状态时出错:`, error)
        appsToRemove.push(name)
      }
    }

    // 执行清理
    for (const name of appsToRemove) {
      await this.unloadMicroApp(name)
      console.log(`已清理微应用缓存: ${name}`)
    }

    console.log(`缓存清理完成，清理了 ${appsToRemove.length} 个应用`)
  }

  /**
   * 启动缓存清理机制
   * 定期清理无效的微应用缓存
   */
  startCacheCleanup() {
    // 如果已经有定时器在运行，先清除它
    if (this.cacheCleanupInterval) {
      clearInterval(this.cacheCleanupInterval);
    }

    // 设置定时器，每5分钟清理一次无效缓存
    this.cacheCleanupInterval = setInterval(() => {
      this.cleanupInvalidCache().catch(error => {
        console.error('定时缓存清理失败:', error);
      });
    }, 5 * 60 * 1000); // 5分钟

    console.log('缓存清理机制已启动，每5分钟清理一次无效缓存');
  }

  /**
   * 初始化全局状态
   */
  initGlobalState() {
    if (!this.globalState) {
      this.globalState = initGlobalState({})
    }
    return this.globalState
  }

  /**
   * 简化版URL可达性检查
   * @param {string} url 要检查的URL
   * @param {number} timeout 超时时间(毫秒)
   */
  async checkUrlReachable(url, timeout = 3000) {
    try {
      console.log(`检查URL可达性: ${url}`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        method: 'HEAD',
        signal: controller.signal,
        mode: 'no-cors',
        cache: 'no-cache'
      });

      clearTimeout(timeoutId);
      console.log(`URL可达性检查成功: ${url}`);
      return response.ok || response.type === 'opaque';
    } catch (error) {
      console.warn(`URL不可达: ${url}`, error.message);
      return false;
    }
  }

  /**
   * 更新访问顺序 (用于LRU缓存)
   * @private
   */
  _updateAccessOrder(appName) {
    // 移除已存在的记录
    const index = this.accessOrder.indexOf(appName);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
    // 添加到末尾（最近访问）
    this.accessOrder.push(appName);

    // 如果超过最大缓存大小，移除最久未访问的
    if (this.accessOrder.length > this.maxCacheSize) {
      const oldest = this.accessOrder.shift();
      if (this.microApps.has(oldest)) {
        console.log(`LRU缓存溢出，移除最久未访问的应用: ${oldest}`);
        this.unloadMicroApp(oldest);
      }
    }
  }

  /**
   * 加载微应用 (简化版)
   * @param {Object} microAppConfig 微应用配置
   * @param {Object} options 加载选项
   */
  async loadMicroApp(microAppConfig, options = {}) {
    const { name, entry } = microAppConfig

    console.log(`[MicroAppManager] 开始加载微应用: ${name}`, {
      entry,
      container: microAppConfig.container,
      existingApps: Array.from(this.microApps.keys()),
      timestamp: new Date().toISOString()
    })

    // 报告开始加载
    this._reportProgress(name, 0, '开始加载微应用');

    // 更新LRU访问顺序
    this._updateAccessOrder(name);

    // 检查是否已经加载
    if (this.microApps.has(name)) {
      const existingApp = this.microApps.get(name)
      if (existingApp) {
        const status = existingApp.getStatus()
        console.log(`微应用 ${name} 已经加载，当前状态: ${status}`)
        this._reportProgress(name, 20, `应用已加载，当前状态: ${status}`);

        // 如果已经是MOUNTED状态，直接返回
        if (status === 'MOUNTED') {
          console.log(`微应用 ${name} 已经加载并挂载`)
          this._reportProgress(name, 100, '应用已加载并挂载');
          return existingApp
        }

        // 如果是NOT_MOUNTED状态，尝试重新挂载
        if (status === 'NOT_MOUNTED') {
          try {
            console.log(`微应用 ${name} 尝试重新挂载`)
            this._reportProgress(name, 30, '尝试重新挂载应用');
            await existingApp.mount()
            this._reportProgress(name, 100, '应用重新挂载成功');
            return existingApp
          } catch (mountError) {
            console.error(`微应用 ${name} 重新挂载失败:`, mountError)
            this._reportProgress(name, 0, '应用重新挂载失败');
            // 挂载失败，从缓存中移除，允许重新加载
            this.microApps.delete(name)
          }
        }

        // 对于其他状态，卸载后重新加载
        if (status !== 'NOT_LOADED' && status !== 'UNMOUNTING' && status !== 'SKIP_BECAUSE_BROKEN') {
          try {
            console.log(`微应用 ${name} 卸载现有实例，状态: ${status}`)
            this._reportProgress(name, 40, '卸载现有实例');
            await existingApp.unmount()
          } catch (unmountError) {
            console.error(`微应用 ${name} 卸载失败:`, unmountError)
          }
          // 从缓存中移除
          this.microApps.delete(name)
        }
      }
    }

    // 检查是否正在加载中
    if (this.loadPromises.has(name)) {
      console.log(`微应用 ${name} 正在加载中，等待完成...`)
      this._reportProgress(name, 10, '应用正在加载中，等待完成...');
      return this.loadPromises.get(name)
    }

    console.log(`开始加载微应用: ${name}`)
    this._reportProgress(name, 5, '开始加载微应用');

    // 创建加载Promise
    const loadPromise = this._loadMicroAppWithRetry(microAppConfig, options)
    this.loadPromises.set(name, loadPromise)

    try {
      const microApp = await loadPromise
      this.microApps.set(name, microApp)
      this.loadPromises.delete(name)
      // 重置失败计数
      this.failureCounts.delete(name)
      console.log(`微应用 ${name} 加载成功`)
      this._reportProgress(name, 100, '应用加载成功');
      return microApp
    } catch (error) {
      this.loadPromises.delete(name)
      console.error(`微应用 ${name} 加载失败:`, error)
      this._reportProgress(name, 0, '应用加载失败');

      // 尝试降级处理
      await this._handleDegradation(name, error);

      throw error
    }
  }

  /**
   * 处理降级方案
   * @private
   */
  async _handleDegradation(appName, error) {
    console.log(`尝试为应用 ${appName} 寻找降级方案`);

    // 触发降级事件，允许外部监听器处理
    if (this.globalState) {
      this.globalState.setGlobalState({
        appError: {
          appName,
          error: error.message || String(error),
          timestamp: Date.now()
        }
      });
    }

    // 检查是否有注册的降级处理函数
    if (this.degradeHandlers && this.degradeHandlers.has(appName)) {
      try {
        const handler = this.degradeHandlers.get(appName);
        await handler(appName, error);
        return;
      } catch (handlerError) {
        console.error(`降级处理函数执行失败: ${appName}`, handlerError);
      }
    }

    // 默认降级处理

    // 可以在这里实现具体的降级逻辑，例如：
    // 1. 显示静态内容
    // 2. 提供离线功能
    // 3. 使用缓存版本
    // 4. 显示友好的错误页面

    // 示例：在容器中显示降级内容
    const frame = document.getElementById('frame');
    if (frame) {
      frame.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 20px; text-align: center;">
          <h2>应用暂时不可用</h2>
          <p>很抱歉，"${appName}" 应用目前无法加载。</p>
          <p>错误信息: ${error.message}</p>
          <p style="margin-top: 20px; font-size: 14px; color: #666;">
            如果问题持续存在，请联系系统管理员。
          </p>
        </div>
      `;
    }
  }

  /**
   * 带重试机制的微应用加载 (简化版)
   * @private
   */
  async _loadMicroAppWithRetry(microAppConfig, options, attempt = 1) {
    const { name, entry } = microAppConfig
    const startTime = Date.now()

    try {
      console.log(`开始加载微应用 ${name} (第 ${attempt} 次尝试):`, entry)
      this._reportProgress(name, 10 + (attempt - 1) * 10, `开始第 ${attempt} 次加载尝试`);

      // 验证并准备容器
      this._reportProgress(name, 15 + (attempt - 1) * 10, '验证并准备容器');
      const frame = await this._validateAndPrepareContainer()

      // 检查URL可达性（仅在第一次尝试时检查）
      if (attempt === 1) {
        this._reportProgress(name, 20, '检查应用入口地址可达性');
        const isReachable = await this.checkUrlReachable(entry, 3000)
        if (!isReachable) {
          throw new Error(`微应用 ${name} 入口地址不可访问: ${entry}`)
        }
        this._reportProgress(name, 25, '应用入口地址检查完成');
      }

      // 创建超时Promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`微应用 ${name} 加载超时 (${this.loadTimeout}ms)`))
        }, this.loadTimeout)
      })

      // 创建加载Promise
      this._reportProgress(name, 30 + (attempt - 1) * 10, '开始加载应用资源');
      const loadPromise = loadMicroApp(microAppConfig, {
        singular: false,
        ...options
      })

      // 等待加载完成或超时
      const microApp = await Promise.race([loadPromise, timeoutPromise])

      // 等待微应用挂载完成
      this._reportProgress(name, 70, '等待应用挂载完成');
      await this._waitForMount(microApp, 5000)

      // 等待 DOM 稳定
      this._reportProgress(name, 85, '等待页面渲染完成');
      const loadStartTime = Date.now()
      const isDomStable = await this._waitForDomStable('frame', loadStartTime)

      if (!isDomStable) {
        console.warn(`微应用 ${name} DOM 稳定性检测超时，但继续执行`)
      }

      const loadTime = Date.now() - startTime
      console.log(`微应用 ${name} 加载成功，耗时: ${loadTime}ms`)
      this._reportProgress(name, 100, '页面渲染完成');

      return microApp
    } catch (error) {
      const loadTime = Date.now() - startTime
      console.error(`微应用 ${name} 加载失败 (第 ${attempt} 次尝试，耗时 ${loadTime}ms):`, error)
      this._reportProgress(name, 0, `加载失败: ${error.message}`);

      // 更新失败计数
      const failureCount = this.failureCounts.get(name) || 0
      this.failureCounts.set(name, failureCount + 1)

      // 判断是否应该重试
      const shouldRetry = this._shouldRetry(error, attempt, name)

      if (shouldRetry) {
        // 指数退避延迟
        const retryDelay = Math.min(1000 * Math.pow(2, attempt - 1), 10000)
        console.log(`微应用 ${name} 将在 ${retryDelay}ms 后重试... (第 ${attempt} 次失败)`)
        this._reportProgress(name, 5 + attempt * 5, `将在 ${retryDelay}ms 后重试`);

        await this._delay(retryDelay)
        return this._loadMicroAppWithRetry(microAppConfig, options, attempt + 1)
      }

      // 重试次数用完，抛出错误
      throw this._enhanceError(error, name, entry)
    }
  }

  /**
   * 判断是否应该重试 (简化版)
   * @private
   */
  _shouldRetry(error, attempt, appName) {
    // 达到最大重试次数
    if (attempt >= this.retryCount) {
      return false
    }

    // 检查连续失败次数
    const failureCount = this.failureCounts.get(appName) || 0
    if (failureCount >= this.maxFailures) {
      console.log(`应用 ${appName} 连续失败次数过多 (${failureCount})，暂停重试`)
      return false
    }

    const errorMessage = error.message || ''

    // 以下错误类型建议重试
    const retryableErrors = [
      'timeout', '超时',
      'network', '网络',
      'fetch', 'Failed to fetch',
      'connection', '连接',
      '不可访问',
      '挂载超时'
    ]

    return retryableErrors.some(retryError =>
      errorMessage.toLowerCase().includes(retryError.toLowerCase())
    )
  }

  /**
   * 等待微应用挂载完成 (简化版)
   * @private
   */
  async _waitForMount(microApp, timeout = 5000) {
    const startTime = Date.now()

    while (Date.now() - startTime < timeout) {
      if (microApp.getStatus() === 'MOUNTED') {
        return true
      }
      await this._delay(100)
    }

    throw new Error(`微应用挂载超时`)
  }

  /**
   * 等待 DOM 稳定（智能版本）
   * 通过监听容器 DOM 变化，判断页面是否真正渲染完成
   * @param {string} containerId 容器ID
   * @param {number} startTime 开始时间戳
   * @returns {Promise<boolean>} 是否稳定（超时返回 false）
   * @private
   */
  async _waitForDomStable(containerId = 'frame', startTime = Date.now()) {
    const config = this.domStableConfig
    const container = document.getElementById(containerId)

    if (!container) {
      console.warn(`容器 #${containerId} 不存在，跳过 DOM 稳定性检测`)
      return true
    }

    console.log(`[DOM 稳定性检测] 开始检测容器 #${containerId}`)

    return new Promise((resolve) => {
      let lastChangeTime = Date.now()
      let isResolved = false

      // 清理函数
      const cleanup = () => {
        if (this.currentObserver) {
          this.currentObserver.disconnect()
          this.currentObserver = null
        }
        if (!isResolved) {
          isResolved = true
          console.log(`[DOM 稳定性检测] 检测结束`)
        }
      }

      // 判断是否应该忽略该节点
      const shouldIgnoreNode = (node) => {
        if (!node || !node.className) return false

        const classNames = node.className?.toString() || ''
        const nodeId = node.id || ''
        const nodeName = node.tagName?.toLowerCase() || ''

        return config.ignoredSelectors.some(selector => {
          // 匹配 class
          if (selector.startsWith('[class*=')) {
            const match = selector.match(/\[class\*="(.+?)"\]/)
            if (match) {
              return classNames.includes(match[1])
            }
          }
          // 匹配 id
          if (selector.startsWith('#')) {
            return nodeId === selector.substring(1)
          }
          // 匹配标签名
          if (selector === nodeName) {
            return true
          }
          // 匹配 class
          if (selector.startsWith('.')) {
            return classNames.includes(selector.substring(1))
          }
          return false
        })
      }

      // 判断变化是否应该被忽略
      const shouldIgnoreMutation = (mutation) => {
        // 检查所有新增的节点
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && !shouldIgnoreNode(node)) {
            return false // 不应该忽略
          }
        }
        // 检查所有移除的节点
        for (const node of mutation.removedNodes) {
          if (node.nodeType === 1 && !shouldIgnoreNode(node)) {
            return false // 不应该忽略
          }
        }
        return true // 应该忽略
      }

      // 创建 MutationObserver
      const observer = new MutationObserver((mutations) => {
        if (isResolved) return

        // 检查是否有"有意义"的 DOM 变化
        const hasMeaningfulChange = mutations.some(mutation => {
          // 忽略字符数据变化
          if (mutation.type === 'characterData') {
            return false
          }
          // 忽略属性变化
          if (mutation.type === 'attributes') {
            const highFreqAttrs = ['style', 'class', 'aria-busy', 'aria-valuenow']
            return !highFreqAttrs.includes(mutation.attributeName)
          }
          // 对于子节点变化，检查是否应该忽略
          if (mutation.type === 'childList') {
            return !shouldIgnoreMutation(mutation)
          }
          return true
        })

        if (hasMeaningfulChange) {
          lastChangeTime = Date.now()
        }
      })

      // 配置观察选项
      const observerOptions = {
        childList: true,      // 观察子节点的添加/删除
        subtree: true,        // 观察所有后代节点
        attributes: false,    // 不观察属性变化（性能考虑）
        characterData: false  // 不观察文本内容变化
      }

      // 开始观察
      observer.observe(container, observerOptions)
      this.currentObserver = observer

      // 检查循环
      const checkStable = () => {
        if (isResolved) return

        const now = Date.now()
        const elapsedTime = now - startTime
        const timeSinceLastChange = now - lastChangeTime

        // 动态计算当前需要的静默时间
        let requiredStableTime
        if (elapsedTime < config.modeSwitchTime) {
          // 前2秒：快速响应
          requiredStableTime = config.initialStableTime
        } else {
          // 2秒后：放宽要求
          requiredStableTime = config.extendedStableTime
        }

        // 检查是否满足稳定性条件
        if (timeSinceLastChange >= requiredStableTime) {
          console.log(`[DOM 稳定性检测] DOM 已稳定，静默时间: ${timeSinceLastChange}ms`)
          cleanup()
          resolve(true)
          return
        }

        // 检查是否超时
        if (elapsedTime >= config.totalTimeout) {
          console.warn(`[DOM 稳定性检测] 超时 (${config.totalTimeout}ms)，强制结束`)
          cleanup()
          resolve(false) // 超时返回 false
          return
        }

        // 继续检查
        setTimeout(checkStable, config.checkInterval)
      }

      // 启动检查
      checkStable()
    })
  }

  /**
   * 验证并准备容器 (简化版)
   * @private
   */
  async _validateAndPrepareContainer() {
    // 等待Vue完成渲染，最多等待3秒
    let attempts = 0
    const maxAttempts = 30 // 3秒，每次等待100ms
    let frame = null

    while (attempts < maxAttempts) {
      frame = document.getElementById('frame')
      if (frame) {
        break
      }

      await this._delay(100)
      attempts++
    }

    if (!frame) {
      throw new Error('#frame 容器未找到，无法挂载微应用')
    }

    // 【修复】先卸载所有已挂载的应用，避免状态冲突导致白屏
    const appsToUnload = []
    for (const [name, app] of this.microApps) {
      const status = app.getStatus()
      if (status === 'MOUNTED') {
        appsToUnload.push(name)
      }
    }

    for (const name of appsToUnload) {
      try {
        console.log(`[MicroAppManager] 准备加载新应用前，先卸载已挂载的应用: ${name}`)
        await this.unloadMicroApp(name)
      } catch (error) {
        console.error(`[MicroAppManager] 卸载应用 ${name} 失败:`, error)
      }
    }

    // 清理容器，准备新的挂载
    this._cleanupContainer()

    return frame
  }

  /**
   * 清理容器状态 (简化版)
   * @private
   */
  _cleanupContainer() {
    const frame = document.getElementById('frame')
    if (!frame) return

    // 移除之前的内容
    frame.innerHTML = ''
    console.log('容器清理完成')
  }

  /**
   * 卸载微应用
   * @param {string} name 微应用名称
   */
  async unloadMicroApp(name) {
    const microApp = this.microApps.get(name)
    if (microApp) {
      try {
        await microApp.unmount()
        this.microApps.delete(name)
        // 从访问顺序中移除
        const index = this.accessOrder.indexOf(name)
        if (index > -1) {
          this.accessOrder.splice(index, 1)
        }
        // 从预加载集合中移除
        this.preloadedApps.delete(name)
        console.log(`微应用 ${name} 卸载成功`)
      } catch (error) {
        console.error(`微应用 ${name} 卸载失败:`, error)
        throw error
      }
    }
  }

  /**
   * 卸载所有微应用
   */
  async unloadAllMicroApps() {
    const unloadPromises = []
    for (const [name] of this.microApps) {
      unloadPromises.push(this.unloadMicroApp(name))
    }
    await Promise.all(unloadPromises)
  }

  /**
   * 预加载微应用
   * @param {Object} microAppConfig 微应用配置
   */
  async preloadMicroApp(microAppConfig) {
    const { name } = microAppConfig

    // 如果已经加载或正在加载，不需要预加载
    if (this.microApps.has(name) || this.loadPromises.has(name) || this.preloadedApps.has(name)) {
      return
    }

    console.log(`开始预加载微应用: ${name}`)
    this.preloadedApps.add(name)

    try {
      // 使用较小的超时时间进行预加载
      const preloadTimeout = Math.min(this.loadTimeout / 2, 5000)

      // 创建预加载Promise（不实际挂载）
      const preloadPromise = loadMicroApp(microAppConfig, {
        singular: false,
        sandbox: false // 预加载时禁用沙箱以提高性能
      })

      // 设置超时
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`微应用 ${name} 预加载超时`))
        }, preloadTimeout)
      })

      // 等待预加载完成或超时
      await Promise.race([preloadPromise, timeoutPromise])
      console.log(`微应用 ${name} 预加载完成`)
    } catch (error) {
      console.error(`微应用 ${name} 预加载失败:`, error)
      this.preloadedApps.delete(name)
    }
  }

  /**
   * 批量预加载微应用
   * @param {Array} microAppConfigs 微应用配置数组
   */
  async preloadMicroApps(microAppConfigs) {
    const preloadPromises = microAppConfigs.map(config =>
      this.preloadMicroApp(config).catch(error => {
        console.error(`预加载微应用 ${config.name} 失败:`, error)
        return null
      })
    )

    // 并行预加载所有应用
    await Promise.all(preloadPromises)
  }

  /**
   * 获取微应用状态
   * @param {string} name 微应用名称
   */
  getMicroAppStatus(name) {
    const microApp = this.microApps.get(name)
    return microApp ? microApp.getStatus() : null
  }

  /**
   * 检查微应用是否已加载
   * @param {string} name 微应用名称
   */
  isMicroAppLoaded(name) {
    return this.microApps.has(name)
  }

  /**
   * 获取微应用的健康状态
   * @param {string} name 微应用名称
   */
  getMicroAppHealth(name) {
    const microApp = this.microApps.get(name)
    if (!microApp) {
      return {
        status: 'NOT_LOADED',
        loaded: false,
        error: null
      }
    }

    try {
      const status = microApp.getStatus()
      const failureCount = this.failureCounts.get(name) || 0

      return {
        status,
        loaded: status === 'MOUNTED',
        failureCount,
        healthy: failureCount < this.maxFailures
      }
    } catch (error) {
      return {
        status: 'ERROR',
        loaded: false,
        error: error.message || String(error)
      }
    }
  }

  /**
   * 获取所有微应用的健康状态
   */
  getAllMicroAppsHealth() {
    const healthMap = {}

    // 检查已加载的应用
    for (const [name] of this.microApps) {
      healthMap[name] = this.getMicroAppHealth(name)
    }

    // 检查预加载的应用
    for (const name of this.preloadedApps) {
      if (!healthMap[name]) {
        healthMap[name] = {
          status: 'PRELOADED',
          loaded: false,
          preloaded: true
        }
      }
    }

    return healthMap
  }

  /**
   * 强制重新加载微应用
   * @param {Object} microAppConfig 微应用配置
   * @param {Object} options 加载选项
   */
  async forceReloadMicroApp(microAppConfig, options = {}) {
    const { name } = microAppConfig

    // 卸载现有应用（如果存在）
    if (this.microApps.has(name)) {
      try {
        const existingApp = this.microApps.get(name)
        await existingApp.unmount()
        this.microApps.delete(name)
        console.log(`微应用 ${name} 已卸载`)
      } catch (unmountError) {
        console.error(`卸载微应用 ${name} 失败:`, unmountError)
      }
    }

    // 重置失败计数
    this.failureCounts.delete(name)

    // 从访问顺序中移除
    const index = this.accessOrder.indexOf(name)
    if (index > -1) {
      this.accessOrder.splice(index, 1)
    }

    // 重新加载应用
    return this.loadMicroApp(microAppConfig, options)
  }
  _enhanceError(error, name, entry) {
    const errorMessage = error.message || ''

    // 根据错误类型返回用户友好的错误信息
    let userMessage = ''
    let errorType = 'unknown'

    if (errorMessage.includes('Failed to fetch')) {
      userMessage = `网络连接失败，无法访问微应用 ${name}`
      errorType = 'network_error'
    } else if (errorMessage.includes('timeout') || errorMessage.includes('超时')) {
      userMessage = `微应用 ${name} 加载超时，请检查网络连接或稍后重试`
      errorType = 'timeout'
    } else if (errorMessage.includes('不可访问')) {
      userMessage = `微应用 ${name} 服务不可用，请联系管理员`
      errorType = 'service_unavailable'
    } else if (errorMessage.includes('died in status LOADING_SOURCE_CODE')) {
      userMessage = `微应用 ${name} 资源加载失败，请检查应用配置`
      errorType = 'resource_error'
    } else {
      userMessage = `微应用 ${name} 加载失败: ${errorMessage}`
      errorType = 'unknown'
    }


    // 记录详细错误信息到控制台
    console.error(`微应用 ${name} 加载失败详情:`, {
      name,
      entry,
      errorType,
      originalError: error,
      userMessage,
      timestamp: new Date().toISOString()
    })

    return new Error(userMessage)
  }

  /**
   * 延迟函数
   * @private
   */
  _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * 清理资源
   */
  destroy() {
    this.unloadAllMicroApps()
    this.loadPromises.clear()
    this.microApps.clear()
    this.preloadedApps.clear()
    this.failureCounts.clear()
    this.accessOrder.length = 0
    this.progressCallbacks.clear()
    this.degradeHandlers.clear()
    this.globalState = null

    // 清理缓存清理定时器
    if (this.cacheCleanupInterval) {
      clearInterval(this.cacheCleanupInterval)
      this.cacheCleanupInterval = null
    }

    // 清理 MutationObserver
    if (this.currentObserver) {
      this.currentObserver.disconnect()
      this.currentObserver = null
    }
  }
}

// 创建单例实例
const microAppManager = new MicroAppManager()

export default microAppManager